import {
  Body,
  Collider,
  Falling,
  PlayerTag,
  Prop,
  Static,
  Transform,
  Velocity,
} from '@/components'
import type { SystemContext } from '@/core/ecs/Scheduler'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, MatchResource } from '@/game/resources'

/** How far below the platform a body is despawned. */
const DESPAWN_DEPTH = -40
/** Longest stagger before a stranded static prop lets go, in seconds. */
const STATIC_FALL_DELAY = 0.45

/**
 * Decides who is still standing on the platform.
 *
 * The arena's signed distance field does all the work: one `contains` call
 * covers circles, chamfered slabs, plus-shaped platforms and rings with a hole
 * in the middle, with no per-arena branching anywhere in this file.
 */
export function arenaBoundsSystem({ world, dt }: SystemContext): void {
  const arena = world.resource(ArenaResource)
  const match = world.resource(MatchResource)
  const events = world.resource(EventsResource)

  if (match.phase === 'fight') {
    const wasCollapsing = arena.collapsing
    arena.update(match.roundTime, dt)
    if (arena.collapsing && !wasCollapsing) {
      events.emit(Events.ArenaCollapse, { scale: arena.scale, starting: true })
    }
  }

  // Bodies that walked (or were thrown) off the edge.
  for (const e of world
    .query([Transform, Velocity, Collider], { none: [Falling, Static] })
    .entities()) {
    // Queries hand out a snapshot taken at the start of the stage, and
    // destruction is deferred to the end of it — so anything an earlier system
    // killed this step is still in this list. A plasma bolt that expired over
    // the void is the first thing in the game to hit that window.
    if (!world.isAlive(e)) continue
    const tr = world.read(e, Transform)
    if (arena.contains(tr.x, tr.z)) continue

    world.add(e, Falling, { timer: 0, spin: 0, reported: false })

    const tag = world.get(e, PlayerTag)
    events.emit(Events.Fall, {
      entity: e,
      player: tag ? tag.player : null,
      x: tr.x,
      z: tr.z,
    })
  }

  // Static scenery stranded outside the shrinking platform falls with it.
  if (arena.collapsing) {
    for (const e of world.query([Transform, Static, Prop]).entities()) {
      const tr = world.read(e, Transform)
      if (arena.contains(tr.x, tr.z)) continue
      world.remove(e, Static)
      if (!world.has(e, Velocity)) world.add(e, Velocity)
      if (!world.has(e, Body)) world.add(e, Body, { mass: 1, invMass: 1 })
      // Staggered by position so a whole row of scenery does not drop in unison.
      const stagger = STATIC_FALL_DELAY * (((Math.abs(tr.x * 7 + tr.z * 13) | 0) % 5) / 4)
      world.add(e, Falling, { timer: -stagger, spin: 0, reported: false })
    }
  }

  // Clean up whatever has dropped out of sight.
  for (const e of world.query([Transform, Falling]).entities()) {
    const tr = world.read(e, Transform)
    if (tr.y < DESPAWN_DEPTH) {
      if (world.has(e, PlayerTag)) {
        // Players are kept alive; the round system decides when to respawn them.
        continue
      }
      world.destroyEntity(e)
    }
  }
}
